<div class="app-event-container">

  <!-- Header -->
  <div class="header-row">
    <h2><i class="pi pi-chart-bar"></i> Application Events</h2>
    <div class="header-actions">
      <button mat-stroked-button color="primary" (click)="toggleAutoRefresh()">
        <i class="pi" [class.pi-pause]="autoRefresh" [class.pi-refresh]="!autoRefresh"></i>
        {{ autoRefresh ? 'Stop Auto-Refresh' : 'Auto-Refresh (30s)' }}
      </button>
      <button mat-stroked-button (click)="exportCsv()">
        <i class="pi pi-download"></i> Export CSV
      </button>
    </div>
  </div>

  <!-- Filter Bar -->
  <div class="filter-bar">
    <div class="filter-row">
      <div class="filter-item">
        <label>From</label>
        <input type="date" [(ngModel)]="dateFrom" (change)="applyFilters()">
      </div>
      <div class="filter-item">
        <label>To</label>
        <input type="date" [(ngModel)]="dateTo" (change)="applyFilters()">
      </div>
      <div class="filter-item">
        <label>Event Type</label>
        <select [(ngModel)]="eventType" (change)="applyFilters()">
          <option value="">All Types</option>
          <option *ngFor="let t of eventTypes" [value]="t">{{ t }}</option>
        </select>
      </div>
      <div class="filter-item">
        <label>Category</label>
        <select [(ngModel)]="eventCategory" (change)="applyFilters()">
          <option value="">All Categories</option>
          <option *ngFor="let c of eventCategories" [value]="c">{{ c }}</option>
        </select>
      </div>
      <div class="filter-item">
        <label>Device</label>
        <select [(ngModel)]="deviceType" (change)="applyFilters()">
          <option value="">All Devices</option>
          <option *ngFor="let d of deviceTypes" [value]="d">{{ d }}</option>
        </select>
      </div>
      <div class="filter-item">
        <label>User Id</label>
        <input type="text" [(ngModel)]="userIdFilter" placeholder="user id" (change)="applyFilters()">
      </div>
      <div class="filter-item">
        <label>Property Id</label>
        <input type="text" [(ngModel)]="propertyIdFilter" placeholder="property id" (change)="applyFilters()">
      </div>
      <div class="filter-item">
        <label>Visitor Id</label>
        <input type="text" [(ngModel)]="visitorIdFilter" placeholder="visitor id" (change)="applyFilters()">
      </div>
      <div class="filter-item">
        <label>Search Query</label>
        <input type="text" [(ngModel)]="searchQueryFilter" placeholder="contains…" (change)="applyFilters()">
      </div>
      <div class="filter-item actions">
        <button mat-raised-button color="primary" (click)="applyFilters()">Apply</button>
        <button mat-button (click)="clearFilters()">Clear</button>
      </div>
    </div>
    <div class="quick-row">
      <span class="quick-label">Quick:</span>
      <button mat-button (click)="quickRange(1)">Today</button>
      <button mat-button (click)="quickRange(7)">7 days</button>
      <button mat-button (click)="quickRange(30)">30 days</button>
      <button mat-button (click)="quickRange(90)">90 days</button>
      <span class="separator">|</span>
      <button mat-button (click)="quickCategory('AUTH')">Auth</button>
      <button mat-button (click)="quickCategory('BOOKING')">Booking</button>
      <button mat-button (click)="quickCategory('AI')">AI</button>
      <button mat-button (click)="quickCategory('SEARCH')">Search</button>
      <button mat-button (click)="quickType('2FA_FAILED')">2FA Failed</button>
      <button mat-button (click)="quickType('LOGIN_FAILED')">Login Failed</button>
    </div>
  </div>

  <!-- Summary Cards -->
  <div class="summary-grid" *ngIf="!summaryLoading">
    <div class="summary-card total">
      <div class="summary-label">Total Events</div>
      <div class="summary-value">{{ summary.total | number }}</div>
      <div class="summary-sub">{{ dateFrom }} → {{ dateTo }}</div>
    </div>
    <div class="summary-card" *ngFor="let cat of categoryList()" [style.border-left-color]="cat.color">
      <div class="summary-label" [style.color]="cat.color">{{ cat.name }}</div>
      <div class="summary-value">{{ cat.count | number }}</div>
      <button mat-button (click)="quickCategory(cat.name)">View</button>
    </div>
  </div>

  <!-- Chart + side panels -->
  <div class="chart-grid">
    <div class="chart-panel">
      <h3>Events over time</h3>
      <div style="height:240px;">
        <p-chart *ngIf="chartData" type="line" [data]="chartData" [options]="chartOptions"></p-chart>
      </div>
    </div>
    <div class="side-panel">
      <h3>Top Event Types</h3>
      <table class="mini-table">
        <tr *ngFor="let t of typeList()">
          <td><span [class]="eventTypeBadgeClass(t.name)">{{ t.name }}</span></td>
          <td class="num">{{ t.count | number }}</td>
          <td><button mat-button (click)="quickType(t.name)">Filter</button></td>
        </tr>
        <tr *ngIf="typeList().length === 0"><td colspan="3" class="empty">No events in range.</td></tr>
      </table>
    </div>
    <div class="side-panel">
      <h3>Top Users</h3>
      <table class="mini-table">
        <tr *ngFor="let u of topUsers">
          <td>{{ u.username || ('#' + u.userId) }}</td>
          <td class="num">{{ u.count | number }}</td>
        </tr>
        <tr *ngIf="topUsers.length === 0"><td colspan="2" class="empty">No user activity.</td></tr>
      </table>
    </div>
  </div>

  <!-- Event Table -->
  <p-table
    #dt
    [value]="events"
    [columns]="columns"
    [lazy]="true"
    (onLazyLoad)="loadLazy($event)"
    [paginator]="true"
    [rows]="rows"
    [totalRecords]="recordTotal"
    [rowsPerPageOptions]="[25, 50, 100, 200]"
    [loading]="loading"
    [globalFilterFields]="['eventType','username','searchQuery','metadata','visitorId']"
    [multiSortMeta]="multiSortMeta"
    sortMode="multiple"
    selectionMode="single"
    (onRowSelect)="onRowSelect($event)"
    dataKey="appEventId"
    styleClass="p-datatable-sm">

    <ng-template pTemplate="caption">
      <div class="p-d-flex p-jc-between" style="display:flex; justify-content:space-between; align-items:center;">
        <span>{{ recordTotal | number }} events</span>
        <span class="p-input-icon-left">
          <i class="pi pi-search"></i>
          <input pInputText type="text" [(ngModel)]="globalFilter" (input)="dt.filterGlobal(globalFilter, 'contains')" placeholder="Global search…" />
        </span>
      </div>
    </ng-template>

    <ng-template pTemplate="header" let-columns>
      <tr>
        <th *ngFor="let col of columns" [pSortableColumn]="col.field" [style.width]="col.width">
          {{ col.header }} <p-sortIcon [field]="col.field"></p-sortIcon>
        </th>
      </tr>
    </ng-template>

    <ng-template pTemplate="body" let-event let-columns="columns">
      <tr [pSelectableRow]="event">
        <td>{{ event.appEventId }}</td>
        <td>{{ event.clickedAt | date:'yyyy-MM-dd HH:mm:ss' }}</td>
        <td><span [class]="eventTypeBadgeClass(event.eventType)">{{ event.eventType }}</span></td>
        <td><span class="cat-chip" [style.background]="categoryColor(event.eventCategory)">{{ event.eventCategory }}</span></td>
        <td>{{ event.username || (event.userId ? '#' + event.userId : '—') }}</td>
        <td>{{ event.deviceType || '' }}</td>
        <td>{{ event.propertyId || '' }}</td>
        <td class="truncate" [title]="event.searchQuery">{{ event.searchQuery || '' }}</td>
        <td class="num">{{ event.responseTimeMs || '' }}</td>
        <td class="truncate" [title]="event.metadata">{{ formatMetadata(event.metadata) }}</td>
      </tr>
    </ng-template>

    <ng-template pTemplate="emptymessage">
      <tr><td colspan="10" class="empty">No events match the current filters.</td></tr>
    </ng-template>
  </p-table>

</div>
